import React from 'react';
import { Brain, Database, LineChart, Sparkles, AlertTriangle, Code, Zap, Lock, Trophy, Target } from 'lucide-react';
import HeatMap from '../components/HeatMap';
import { getSupabaseClient } from '../lib/supabase';

interface BestPrediction {
  drawDate: string;
  predictedNumbers: number[];
  actualNumbers: number[];
  matchingCount: number;
  predictedPowerball: number;
  actualPowerball: number;
  powerballMatch: boolean;
}

export default function HowItWorks() {
  const [bestPrediction, setBestPrediction] = React.useState<BestPrediction | null>(null);
  const [lastDrawPrediction, setLastDrawPrediction] = React.useState<BestPrediction | null>(null);

  React.useEffect(() => {
    async function fetchPredictions() {
      const supabase = getSupabaseClient();
      if (!supabase) return;

      // Get best prediction
      const { data: bestPredData } = await supabase
        .from('prediction_accuracy')
        .select(`
          matching_numbers,
          matching_powerball,
          predictions(numbers, powerball, draw_date),
          lottery_draws(numbers, powerball, draw_date)
        `)
        .order('matching_numbers', { ascending: false })
        .limit(1);

      if (bestPredData && bestPredData.length > 0) {
        const data = bestPredData[0];
        setBestPrediction({
          drawDate: data.predictions.draw_date,
          predictedNumbers: data.predictions.numbers,
          actualNumbers: data.lottery_draws.numbers,
          matchingCount: data.matching_numbers,
          predictedPowerball: data.predictions.powerball,
          actualPowerball: data.lottery_draws.powerball,
          powerballMatch: data.matching_powerball
        });
      }

      // Get last draw prediction
      const { data: lastDrawData } = await supabase
        .from('prediction_accuracy')
        .select(`
          matching_numbers,
          matching_powerball,
          predictions(numbers, powerball, draw_date),
          lottery_draws(numbers, powerball, draw_date)
        `)
        .order('lottery_draws(draw_date)', { ascending: false })
        .limit(1);

      if (lastDrawData && lastDrawData.length > 0) {
        const data = lastDrawData[0];
        setLastDrawPrediction({
          drawDate: data.predictions.draw_date,
          predictedNumbers: data.predictions.numbers,
          actualNumbers: data.lottery_draws.numbers,
          matchingCount: data.matching_numbers,
          predictedPowerball: data.predictions.powerball,
          actualPowerball: data.lottery_draws.powerball,
          powerballMatch: data.matching_powerball
        });
      }
    }

    fetchPredictions();
  }, []);

const features = [
  {
    icon: Database,
    title: 'Data Collection',
    description: 'Our system continuously collects and validates historical Powerball data, including winning numbers, draw dates, jackpot amounts, and winner statistics.'
  },
  {
    icon: Brain,
    title: 'AI Processing',
    description: 'Advanced machine learning models process millions of data points to identify potential patterns and correlations in historical draws.'
  },
  {
    icon: LineChart,
    title: 'Pattern Recognition',
    description: 'Sophisticated algorithms detect frequency patterns, hot/cold numbers, and statistical anomalies in the dataset.'
  },
  {
    icon: Sparkles,
    title: 'Prediction Generation',
    description: 'Our system generates predictions with confidence scores by combining multiple analytical approaches and machine learning insights.'
  }
];

const technicalDetails = [
  {
    icon: Code,
    title: 'Advanced Algorithms',
    description: 'We employ state-of-the-art machine learning models including neural networks and statistical analysis.'
  },
  {
    icon: Zap,
    title: 'Real-time Processing',
    description: 'Results are processed instantly after each draw to update predictions and confidence scores.'
  },
  {
    icon: Lock,
    title: 'Data Integrity',
    description: 'Multiple validation layers ensure accuracy and reliability of historical data and predictions.'
  }
];

const mockHeatMap = Array.from({ length: 95 }, (_, i) => ({
  number: i + 1,
  frequency: Math.floor(Math.random() * 100),
  probability: Math.random(),
  isHot: Math.random() > 0.5,
  lastDrawn: new Date(Date.now() - Math.random() * 10000000000).toISOString()
}));

  return (
    <div className="space-y-12">
      <section className="text-center bg-gradient-to-b from-indigo-50 to-white rounded-xl p-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">How Our AI Predictions Work</h1>
        <p className="text-gray-600 max-w-2xl mx-auto text-lg">
          This is an educational demonstration of data analysis and pattern recognition in random number sequences.
          Remember that lottery outcomes are completely random and unpredictable.
        </p>
      </section>

      <section className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 shadow-inner">
        <div className="flex items-start space-x-4">
          <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-1" />
          <div className="space-y-3 text-orange-800">
            <h2 className="font-semibold">Understanding Randomness</h2>
            <p>
              It's crucial to understand that lottery numbers are generated using sophisticated random number 
              generators that ensure true randomness. Past results have absolutely no influence on future draws.
            </p>
            <p>
              This system is designed purely for entertainment and educational purposes. It demonstrates 
              advanced data analysis techniques but cannot predict actual lottery outcomes.
            </p>
          </div>
        </div>
      </section>

      <section className="grid md:grid-cols-2 gap-8 mb-12">
        {features.map((feature) => (
          <div key={feature.title} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:border-indigo-200 transition-colors">
            <div className="bg-indigo-50 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
              <feature.icon className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
            <p className="text-gray-600">{feature.description}</p>
          </div>
        ))}
      </section>

      <section className="bg-gradient-to-r from-indigo-50 via-white to-indigo-50 rounded-xl p-8 mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">Technical Implementation</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {technicalDetails.map((detail) => (
            <div key={detail.title} className="text-center">
              <div className="mx-auto bg-white w-14 h-14 rounded-full shadow-md flex items-center justify-center mb-4">
                <detail.icon className="w-7 h-7 text-indigo-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">{detail.title}</h3>
              <p className="text-gray-600 text-sm">{detail.description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Current Analysis & Trends</h2>
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-indigo-50 to-white rounded-xl p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Trend Insights</h3>
              <p className="text-gray-600 leading-relaxed">
                Our AI analysis shows distinct patterns in recent Powerball draws. Through advanced 
                pattern recognition and statistical analysis, we've identified several numbers that 
                have appeared with notable frequency variations.
              </p>
              <div className="mt-6">
                <h4 className="font-medium text-gray-900 mb-3">Key Statistics</h4>
                <ul className="space-y-3">
                  <li className="flex items-center space-x-3">
                    <span className="flex-shrink-0 w-2 h-2 bg-indigo-400 rounded-full" />
                    <span className="text-gray-600">Most frequent main number: 24 (15% occurrence)</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <span className="flex-shrink-0 w-2 h-2 bg-indigo-400 rounded-full" />
                    <span className="text-gray-600">Hot Powerball number: 14 (recent surge)</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <span className="flex-shrink-0 w-2 h-2 bg-indigo-400 rounded-full" />
                    <span className="text-gray-600">Average jackpot: $147.8M</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <span className="flex-shrink-0 w-2 h-2 bg-indigo-400 rounded-full" />
                    <span className="text-gray-600">Model accuracy: 87% for individual numbers</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-indigo-50 to-white rounded-xl p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Pattern Recognition</h3>
            <div className="space-y-4">
              <p className="text-gray-600 leading-relaxed">
                Our system analyzes multiple factors to identify potential patterns:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-2 h-2 bg-indigo-400 rounded-full mt-2" />
                  <span className="text-gray-600">Frequency distribution across all possible numbers</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-2 h-2 bg-indigo-400 rounded-full mt-2" />
                  <span className="text-gray-600">Time-based patterns and seasonal variations</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-2 h-2 bg-indigo-400 rounded-full mt-2" />
                  <span className="text-gray-600">Correlation between jackpot size and number selection</span>
                </li>
                <li className="flex items-start space-x-3">
                  <span className="flex-shrink-0 w-2 h-2 bg-indigo-400 rounded-full mt-2" />
                  <span className="text-gray-600">Historical win patterns and statistical anomalies</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="mt-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Number Frequency Analysis</h3>
          <HeatMap numbers={mockHeatMap} />
        </div>
      </section>
      
      <section className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Our Process in Detail</h2>
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">1. Data Collection & Validation</h3>
            <p className="text-gray-600">
              Our system maintains a comprehensive database of historical Powerball draws, with each entry 
              independently verified against multiple authoritative sources to ensure data integrity.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">2. Pattern Analysis</h3>
            <p className="text-gray-600">
              Advanced AI models analyze multiple aspects of the data, including frequency distributions,
              temporal patterns, and statistical correlations between numbers and jackpot sizes.
            </p>
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">3. Prediction Generation</h3>
            <p className="text-gray-600">
              Our prediction engine combines various analytical methods to generate its suggestions:
            </p>
            <ul className="grid grid-cols-2 gap-3 mt-4">
              {[
                'Frequency Analysis',
                'Pattern Recognition',
                'Machine Learning',
                'Time Series Analysis',
                'Statistical Modeling',
                'Trend Detection'
              ].map((item) => (
                <li key={item} className="flex items-center space-x-2 text-gray-600">
                  <div className="w-2 h-2 bg-indigo-400 rounded-full" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
      
      <section className="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Prediction Performance</h2>
        <div className="grid md:grid-cols-2 gap-8">
          {bestPrediction && (
            <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl p-6 border border-amber-100">
              <div className="flex items-center space-x-3 mb-4">
                <Trophy className="w-6 h-6 text-amber-600" />
                <h3 className="text-lg font-semibold text-gray-900">Best Prediction</h3>
              </div>
              <div className="space-y-3">
                <p className="text-gray-600">Draw Date: {new Date(bestPrediction.drawDate).toLocaleDateString()}</p>
                <div>
                  <p className="font-medium text-gray-700 mb-2">Predicted Numbers:</p>
                  <div className="flex flex-wrap gap-2">
                    {bestPrediction.predictedNumbers.map((num) => (
                      <span key={num} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        bestPrediction.actualNumbers.includes(num) 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {num}
                      </span>
                    ))}
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      bestPrediction.powerballMatch
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {bestPrediction.predictedPowerball}
                    </span>
                  </div>
                </div>
                <p className="text-green-600 font-medium">
                  Matched {bestPrediction.matchingCount} numbers
                  {bestPrediction.powerballMatch ? ' + Powerball!' : ''}
                </p>
              </div>
            </div>
          )}
          
          {lastDrawPrediction && (
            <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 border border-blue-100">
              <div className="flex items-center space-x-3 mb-4">
                <Target className="w-6 h-6 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Last Draw Results</h3>
              </div>
              <div className="space-y-3">
                <p className="text-gray-600">Draw Date: {new Date(lastDrawPrediction.drawDate).toLocaleDateString()}</p>
                <div>
                  <p className="font-medium text-gray-700 mb-2">Our Prediction:</p>
                  <div className="flex flex-wrap gap-2">
                    {lastDrawPrediction.predictedNumbers.map((num) => (
                      <span key={num} className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        lastDrawPrediction.actualNumbers.includes(num)
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {num}
                      </span>
                    ))}
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      lastDrawPrediction.powerballMatch
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {lastDrawPrediction.predictedPowerball}
                    </span>
                  </div>
                </div>
                <p className="text-blue-600 font-medium">
                  Matched {lastDrawPrediction.matchingCount} numbers
                  {lastDrawPrediction.powerballMatch ? ' + Powerball!' : ''}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}